/**
 * Componente: AlertDetailModal
 * Modal con el detalle completo de una alerta de cliente multi-nube.
 * Muestra descripción, umbrales superados, metadatos y toda la información disponible.
 */

import React from 'react';
import { X, AlertTriangle, Activity, Tag, Info } from 'lucide-react';

const SEVERITY_STYLES = {
  CRITICAL: { color: '#ef4444', bg: 'rgba(239,68,68,0.14)', border: 'rgba(239,68,68,0.5)' },
  HIGH: { color: '#f97316', bg: 'rgba(249,115,22,0.13)', border: 'rgba(249,115,22,0.45)' },
  MEDIUM: { color: '#facc15', bg: 'rgba(250,204,21,0.12)', border: 'rgba(250,204,21,0.4)' },
  LOW: { color: '#4ade80', bg: 'rgba(74,222,128,0.12)', border: 'rgba(74,222,128,0.4)' },
  INFO: { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.4)' }
};

const CLOUD_COLORS = {
  aws: '#fb923c',
  azure: '#38bdf8',
  gcp: '#34d399',
  huawei: '#f87171',
  m365: '#f472b6'
};

const SEVERITY_LABELS = {
  CRITICAL: '🔴 P1 – Escalar inmediato y abrir bridge operativo',
  HIGH: '🟠 P2 – Atender de forma prioritaria y asignar responsable',
  MEDIUM: '🟡 P3 – Revisar impacto y programar mitigación',
  LOW: '🟢 P4 – Monitorear y ajustar en ventana operativa',
  INFO: '🔵 P5 – Solo seguimiento, sin acción inmediata'
};

// Intenta parsear JSON embebido en un string (para campos tipo metadata/description)
const tryParseJson = (value) => {
  if (typeof value !== 'string') return null;
  const str = value.trim();
  if (!str.startsWith('{') && !str.startsWith('[')) return null;
  try { return JSON.parse(str); } catch { return null; }
};

// Renderiza el valor de un campo de forma inteligente
const FieldValue = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="italic text-violet-200/40">—</span>;
  }
  const parsed = tryParseJson(value);
  if (parsed) {
    return (
      <pre className="max-h-48 overflow-auto rounded-lg bg-black/30 p-3 text-[11px] leading-relaxed text-violet-100/90 custom-scrollbar">
        {JSON.stringify(parsed, null, 2)}
      </pre>
    );
  }
  return <span className="break-words text-violet-100/90">{String(value)}</span>;
};

// Sección con título e icono
const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-cyan-300/80" />
      <h3 className="text-xs font-semibold uppercase tracking-wider text-violet-200/70">{title}</h3>
    </div>
    {children}
  </div>
);

// Fila de detalle
const Row = ({ label, value, highlight }) => (
  <div className="flex items-start gap-3 rounded-lg px-3 py-2 odd:bg-violet-500/5">
    <span className="w-36 shrink-0 text-xs text-violet-200/55">{label}</span>
    <span
      className={`flex-1 text-xs ${highlight ? 'font-semibold' : ''}`}
      style={highlight ? { color: highlight } : {}}
    >
      <FieldValue value={value} />
    </span>
  </div>
);

export default function AlertDetailModal({ alert, onClose }) {
  if (!alert) return null;

  const severity = (alert.severity_normalized || 'INFO').toUpperCase();
  const sevStyle = SEVERITY_STYLES[severity] || SEVERITY_STYLES.INFO;
  const cloud = (alert.nube || alert.provider || '').toLowerCase();
  const cloudColor = CLOUD_COLORS[cloud] || '#a78bfa';

  const formatDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('es-MX', { dateStyle: 'full', timeStyle: 'medium' });
  };

  // Campos conocidos con etiquetas amigables
  const mainFields = [
    { label: 'Severidad', key: 'severity_normalized' },
    { label: 'Estado', key: 'status_normalized' },
    { label: 'Activa', key: null, value: alert.is_active !== undefined ? (alert.is_active ? 'Sí' : 'No') : '—' },
    { label: 'Proveedor', key: 'nube' },
    { label: 'Servicio', key: 'service' },
    { label: 'Recurso', key: 'resource_name' },
    { label: 'ID Recurso', key: 'resource_id' },
    { label: 'Proyecto ID', key: 'project_id' },
    { label: 'Cliente', key: 'cliente' },
    { label: 'Región', key: 'region' },
    { label: 'Fecha/Hora', key: null, value: formatDate(alert.event_ts) },
  ];

  // Campos de contexto / diagnóstico (description, message, reason, threshold, raw_alert, metadata, etc.)
  const diagnosticKeys = [
    'description', 'message', 'reason', 'details',
    'threshold', 'threshold_value', 'current_value', 'metric',
    'metric_name', 'metric_value', 'condition', 'condition_type',
    'raw_alert', 'metadata', 'additional_info', 'context',
    'error_message', 'error_code', 'recommendation', 'impact',
    'affected_services', 'duration', 'count', 'cantidad'
  ];

  // Campos extra que vengan de la tabla y no estén en los listados anteriores
  const knownKeys = new Set([
    'fecha', 'hora', 'nube', 'provider', 'service', 'severity_normalized', 'severity_score',
    'status_normalized', 'is_active', 'region', 'resource_id', 'resource_name',
    'project_id', 'cliente', 'event_ts', ...diagnosticKeys
  ]);
  const extraKeys = Object.keys(alert).filter(k => !knownKeys.has(k) && alert[k] !== null && alert[k] !== undefined && alert[k] !== '');

  const hasDiagnostic = diagnosticKeys.some(k => alert[k] !== null && alert[k] !== undefined && alert[k] !== '');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(7,2,20,0.82)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="custom-scrollbar relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-y-auto rounded-2xl border border-violet-500/30 bg-[#160a2e] shadow-[0_32px_80px_rgba(0,0,0,0.6)]"
      >
        {/* Header del modal */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-violet-500/20 bg-[#160a2e]/95 px-6 py-5 backdrop-blur-sm">
          <div className="flex flex-wrap items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-cyan-300/80" />
            <h2 className="text-base font-bold text-violet-100">Detalle de Alerta</h2>
            {/* Badge de severidad */}
            <span
              className="rounded-full px-3 py-0.5 text-xs font-bold"
              style={{ color: sevStyle.color, background: sevStyle.bg, border: `1px solid ${sevStyle.border}` }}
            >
              {severity}
            </span>
            {/* Badge de nube */}
            <span
              className="rounded-full px-3 py-0.5 text-xs font-bold"
              style={{ color: cloudColor, background: `${cloudColor}1f`, border: `1px solid ${cloudColor}55` }}
            >
              {alert.nube || alert.provider || '—'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl border border-violet-400/30 bg-violet-500/20 p-1.5 text-violet-200 transition hover:bg-violet-500/40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Acción recomendada */}
        {SEVERITY_LABELS[severity] && (
          <div
            className="mx-6 mt-5 rounded-xl px-4 py-3 text-xs font-semibold"
            style={{ background: sevStyle.bg, border: `1px solid ${sevStyle.border}`, color: sevStyle.color }}
          >
            {SEVERITY_LABELS[severity]}
          </div>
        )}

        <div className="flex flex-col gap-6 px-6 py-5">

          {/* Sección: Identificación */}
          <Section icon={Tag} title="Identificación y recurso">
            <div className="rounded-xl border border-violet-500/15 bg-black/20 overflow-hidden">
              {mainFields.map(({ label, key, value }) => {
                const val = key ? alert[key] : value;
                const isHighlight = label === 'Severidad' ? sevStyle.color :
                  label === 'Proveedor' ? cloudColor : null;
                return <Row key={label} label={label} value={val} highlight={isHighlight} />;
              })}
            </div>
          </Section>

          {/* Sección: Diagnóstico */}
          {hasDiagnostic && (
            <Section icon={Activity} title="Diagnóstico y detalle operativo">
              <div className="rounded-xl border border-violet-500/15 bg-black/20 overflow-hidden">
                {diagnosticKeys
                  .filter(k => alert[k] !== null && alert[k] !== undefined && alert[k] !== '')
                  .map(k => (
                    <div key={k} className="flex flex-col gap-1 px-3 py-2.5 odd:bg-violet-500/5">
                      <span className="text-xs text-violet-200/55 capitalize">{k.replace(/_/g, ' ')}</span>
                      <div className="text-xs">
                        <FieldValue value={alert[k]} />
                      </div>
                    </div>
                  ))
                }
              </div>
            </Section>
          )}

          {/* Sección: Campos adicionales */}
          {extraKeys.length > 0 && (
            <Section icon={Info} title="Campos adicionales">
              <div className="rounded-xl border border-violet-500/15 bg-black/20 overflow-hidden">
                {extraKeys.map(k => (
                  <div key={k} className="flex flex-col gap-1 px-3 py-2.5 odd:bg-violet-500/5">
                    <span className="text-xs text-violet-200/55 capitalize">{k.replace(/_/g, ' ')}</span>
                    <div className="text-xs">
                      <FieldValue value={alert[k]} />
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Sin diagnóstico disponible */}
          {!hasDiagnostic && extraKeys.length === 0 && (
            <div className="rounded-xl border border-violet-500/15 bg-black/10 px-4 py-6 text-center text-xs text-violet-200/50">
              No hay información de diagnóstico adicional disponible para esta alerta.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
